import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { StatutCommande } from '../api/types';
import { STATUT_COMMANDE, STATUT_COMMANDE_VARIANT } from '../lib/labels';
import { formatDayChip, formatEventSubtitle } from '../lib/format';
import { Badge } from './ui';

/**
 * Carte événement de l'app Fan Wallet (docs/design-tokens.md § Carte événement).
 * Aucun visuel réel n'est disponible côté BO : on rend un dégradé déterministe,
 * dérivé du nom de la manifestation (le seul identifiant présent dans
 * `CommandeSummary` comme dans `Commande`), pour qu'un même événement garde la
 * même couleur d'un écran à l'autre.
 */
const GRADIENTS = [
  'linear-gradient(150deg, #a3141a 0%, #4a0c10 100%)',
  'linear-gradient(150deg, #1d4ea8 0%, #0b2257 100%)',
  'linear-gradient(150deg, #6a24a8 0%, #2e0e52 100%)',
  'linear-gradient(150deg, #137070 0%, #083236 100%)',
  'linear-gradient(150deg, #8a4f0f 0%, #3a2105 100%)',
];

const OVERLAY = 'linear-gradient(180deg, rgba(31,35,43,0) 60%, rgba(31,35,43,0.85) 100%)';

function isToday(iso: string | null): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return date.toDateString() === now.toDateString();
}

export function gradientFor(key: string | number): string {
  const source = String(key);
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) hash = (hash * 31 + source.charCodeAt(i)) % 100000;
  return GRADIENTS[hash % GRADIENTS.length];
}

export interface EventCardProps {
  title: string;
  dateSeance: string | null;
  /** Clé du dégradé : libellé de la manifestation (ou idManif). */
  gradientKey: string | number;
  statut: StatutCommande;
  lieu?: string | null;
  ville?: string | null;
  flagAction?: boolean;
  flagActionCodeBarre?: boolean;
  /** Ligne 12px en pied de carte : « #idCommande · N billets · politique ». */
  footer?: ReactNode;
  /** Ligne libre supplémentaire (titulaire, guid…). */
  meta?: ReactNode;
  children?: ReactNode;
  /** Barre au-dessus des chips (fiche commande : retour, n° de commande, suppression). */
  topBar?: ReactNode;
  /** `compact` : titre plus petit et pas de chip date (en-tête de la fiche commande). */
  size?: 'default' | 'compact';
  to?: string;
  className?: string;
}

export default function EventCard({
  title,
  dateSeance,
  gradientKey,
  statut,
  lieu,
  ville,
  flagAction = false,
  flagActionCodeBarre = false,
  footer,
  meta,
  children,
  topBar,
  size = 'default',
  to,
  className = '',
}: EventCardProps) {
  const { day, month } = formatDayChip(dateSeance);
  const flagged = flagAction || flagActionCodeBarre;
  const aujourdhui = isToday(dateSeance);
  const compact = size === 'compact';

  const chips =
    aujourdhui || statut !== 0 ? (
      <>
        {aujourdhui ? <Badge variant="info">Aujourd’hui !</Badge> : null}
        {statut !== 0 ? <Badge variant={STATUT_COMMANDE_VARIANT[statut]}>{STATUT_COMMANDE[statut]}</Badge> : null}
      </>
    ) : null;

  const dateChip = (
    <div
      className="flex w-[61px] shrink-0 flex-col items-center rounded bg-white px-4 py-3 text-bg"
    >
      <span className="text-2xl/6 font-bold tracking-[0.48px]">{day}</span>
      <span className="text-sm/4 font-semibold uppercase">{month}</span>
    </div>
  );

  const card = (
    <article
      data-testid="event-card"
      className={`relative flex h-full ${compact ? '' : 'min-h-56'} flex-col justify-between overflow-hidden rounded-lg p-4 pb-5 shadow-elevation-3 ${className}`}
      style={{ backgroundImage: `${OVERLAY}, ${gradientFor(gradientKey)}` }}
    >
      {topBar ? <div className="-mx-4 -mt-4 mb-4 border-b border-white/15 px-4 py-2.5">{topBar}</div> : null}
      {compact ? (
        // Compact (fiche commande) : pas de chip date (la date est dans le sous-titre), chips seulement s'il y en a.
        <>
          {chips ? <div className="mb-2 flex flex-wrap items-start gap-1.5">{chips}</div> : null}
          <h3 className="text-lg/6 font-bold uppercase tracking-[0.48px] text-white">{title}</h3>
          <p className="mt-1 text-sm/4 text-on-surface-variant">{formatEventSubtitle(dateSeance, lieu, ville)}</p>
        </>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-start gap-1.5">{chips}</div>
          {dateChip}
        </div>
      )}

      <div className={compact ? '' : 'mt-6'}>
        {compact ? null : <h3 className="text-2xl/8 font-bold uppercase tracking-[0.48px] text-white">{title}</h3>}
        {compact ? null : (
          <p className="mt-1 text-sm/4 text-on-surface-variant">{formatEventSubtitle(dateSeance, lieu, ville)}</p>
        )}
        {meta ? <p className="mt-2 text-xs/4 text-outline-variant">{meta}</p> : null}
        {children}
        {footer ? <p className="mt-2 text-xs/4 text-outline">{footer}</p> : null}
        {flagged ? (
          <p className="mt-2 flex items-center gap-1.5 text-xs/4 text-white">
            <span aria-hidden className="inline-block size-2 rounded-full bg-error" />
            {flagActionCodeBarre ? 'Rechargement (QR inclus) en attente' : 'Rechargement en attente'}
          </p>
        ) : null}
      </div>
    </article>
  );

  if (!to) return card;
  return (
    <Link to={to} className="block rounded-lg focus-visible:outline-offset-4">
      {card}
    </Link>
  );
}
